import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import OnboardingLayout from '../../components/layouts/onboarding/OnboardingLayout';
import storage from '../../utils/storage';
import {
  Form,
  FormSection,
  InputWrapper,
  CompanyDisplay,
  CompanyValue,
  FormContainer,
  FormFields,
  ButtonContainer,
  FlexRow
} from '../../styles/components';
import InputField from '../../components/common/InputField';
import PrimaryButton from '../../components/common/PrimaryButton';
import { colors, typography } from '../../styles/theme/index';

const Disclosure = styled.p`
  font-family: ${typography.fontFamily};
  font-size: ${typography.fontSize.sm};
  color: ${colors.text.secondary};
  margin: 16px 0 40px;
  font-weight: ${typography.fontWeight.bold};
`;

const OfficerSection = styled.div`
  margin-top: 24px;
  padding-top: 16px;
  border-top: 1px solid ${colors.border};
`;

const OfficerBlock = styled.div`
  margin-bottom: 16px;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const OfficerSecondRow = styled.div`
  display: flex;
  gap: 16px;
  width: 100%;
  margin-top: -12px;

  > div:first-child {
    width: 180px;
    flex-shrink: 0;
  }

  > div:last-child {
    flex: 1;
  }

  ${InputWrapper} {
    margin-bottom: 16px;
  }
`;

const OnboardingStep5 = () => {
  const navigate = useNavigate();
  const [companyName, setCompanyName] = useState('');
  const [organizationType, setOrganizationType] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    companyNumber: '',
    registeredAddress: '',
    sicCodes: ' ',
    officerCount: '',
    officers: []
  });

  useEffect(() => {
    // Check for required data from previous steps
    const step3Data = storage.getItem('step3Data');
    if (step3Data) {
      const { companyName } = JSON.parse(step3Data);
      setCompanyName(companyName);
    } else {
      // If we don't have company data, go back to step 3
      navigate('/onboarding/step-three');
      return;
    }
    
    // First try to get prefilled data from step 4 or previously saved step 5 data
    let initialFormData = formData;
    const prefillData = storage.getItem('step5Prefill');
    if (prefillData) {
      initialFormData = JSON.parse(prefillData);
    } else {
      const step5Data = storage.getItem('step5Data');
      if (step5Data) {
        initialFormData = JSON.parse(step5Data);
      }
    }

    // Ensure sicCodes has a value
    initialFormData.sicCodes = initialFormData.sicCodes || ' ';

    // Load organization type and set appropriate defaults
    const orgType = storage.getItem('selectedOrganisationType');
    if (orgType) {
      setOrganizationType(orgType);
      // If it's a sole trader, override with default values
      if (orgType.toLowerCase() === 'sole-trader') {
        initialFormData = {
          ...initialFormData,
          companyNumber: '0',
          sicCodes: 'N/A - Sole Trader',
          officerCount: '0',
          officers: [{
            name: '0',
            appointedOn: '0',
            occupation: '0'
          }]
        };
      }
    }

    // Set the final form data
    setFormData(initialFormData);
    
  }, [navigate]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    const newFormData = {
      ...formData,
      [name]: value
    };
    setFormData(newFormData);
    // Store form data as user types
    storage.setItem('step5Data', JSON.stringify(newFormData));
  };

  const handleOfficerChange = (index, field, value) => {
    const newFormData = {
      ...formData,
      officers: formData.officers.map((officer, i) => 
        i === index ? { ...officer, [field]: value } : officer
      )
    };
    setFormData(newFormData);
    // Store form data as user updates officers
    storage.setItem('step5Data', JSON.stringify(newFormData));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      const pipedriveData = storage.getPipedriveData();
      if (!pipedriveData?.organizationId) {
        throw new Error('Organization ID not found');
      }

      // Send data to backend
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/onboarding/step-five`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...formData,
          organizationId: pipedriveData.organizationId
        })
      });

      const result = await response.json();
      if (!result.success) {
        throw new Error(result.error || 'Failed to process step 5');
      }

      // Store form data
      storage.setItem('step5Data', JSON.stringify(formData));

      // Navigate to next step
      navigate('/onboarding/step-six');
    } catch (error) {
      console.error('Form submission error:', error);
      setError(error.message || 'There was an error processing your request. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBack = () => {
    // Store current form data before navigating back
    storage.setItem('step5Data', JSON.stringify(formData));
    navigate('/onboarding/step-four');
  };

  return (
    <OnboardingLayout
      title="Your"
      brandText="Organisation Details Cont."
      onBack={handleBack}
    >
      <FormContainer>
        <CompanyDisplay>
          <CompanyValue>{companyName}</CompanyValue>
        </CompanyDisplay>

        <Form onSubmit={handleSubmit}>
          {error && (
            <div style={{ color: 'red', marginBottom: '1rem' }}>
              {error}
            </div>
          )}
          <FormFields>
            {(!organizationType || organizationType.toLowerCase() !== 'sole-trader') && (
              <Disclosure>
                Please confirm these details match Companies House records and amend if necessary:
              </Disclosure>
            )}

            {(!organizationType || organizationType.toLowerCase() !== 'sole-trader') && (
              <>
                <div style={{ position: 'relative' }}>
                  <InputField
                    label="Company Number"
                    name="companyNumber"
                    type="text"
                    value={formData.companyNumber}
                    onChange={handleInputChange}
                    disabled
                  />
                </div>

                <div style={{ position: 'relative' }}>
                  <InputField
                    label="Nature of Business (SIC Codes)"
                    name="sicCodes"
                    type="text"
                    value={formData.sicCodes}
                    onChange={handleInputChange}
                    placeholder="Enter SIC codes"
                  />
                </div>

                <div style={{ position: 'relative' }}>
                  <InputField
                    label="Number of Officers"
                    name="officerCount"
                    type="text"
                    value={formData.officerCount}
                    onChange={handleInputChange}
                    disabled
                  />
                </div>
              </>
            )}

            <div style={{ position: 'relative' }}>
              <InputField
                label="Registered Business Address"
                name="registeredAddress"
                type="text"
                value={formData.registeredAddress}
                onChange={handleInputChange}
                placeholder="Enter registered address"
              />
            </div>

            {(!organizationType || organizationType.toLowerCase() !== 'sole-trader') && (
              <OfficerSection>
                {formData.officers.map((officer, index) => (
                  <OfficerBlock key={index}>
                    <FlexRow>
                      <div style={{ position: 'relative', width: '100%' }}>
                        <InputField
                          label={`Officer ${index + 1} Name`}
                          value={officer.name}
                          onChange={(e) => handleOfficerChange(index, 'name', e.target.value)}
                          type="text"
                          placeholder="Enter officer name"
                        />
                      </div>
                    </FlexRow>
                    <OfficerSecondRow>
                      <div style={{ position: 'relative' }}>
                        <InputField
                          label="Appointed On"
                          value={officer.appointedOn}
                          onChange={(e) => handleOfficerChange(index, 'appointedOn', e.target.value)}
                          type="text"
                          placeholder="Enter appointment date"
                        />
                      </div>
                      <div style={{ position: 'relative' }}>
                        <InputField
                          label="Occupation"
                          value={officer.occupation}
                          onChange={(e) => handleOfficerChange(index, 'occupation', e.target.value)}
                          type="text"
                          placeholder="Enter occupation"
                        />
                      </div>
                    </OfficerSecondRow>
                  </OfficerBlock>
                ))}
              </OfficerSection>
            )}
          </FormFields>

          <ButtonContainer>
            <PrimaryButton 
              type="submit"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Processing...' : 'Continue'}
            </PrimaryButton>
          </ButtonContainer>
        </Form>
      </FormContainer>
    </OnboardingLayout>
  );
};

export default OnboardingStep5; 